import AlertTitle from '@mui/material/AlertTitle/AlertTitle';
import Alert from '@mui/material/Alert/Alert';
import Button from '@mui/material/Button/Button';
import Container from '@mui/material/Container/Container';
import Stack from '@mui/material/Stack/Stack';
import Toolbar from '@mui/material/Toolbar/Toolbar';
import { Component, ContextType, ErrorInfo, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { appContext } from '../../Contexts/AppContext';
import { errorHandler } from '../error/error-handler';

interface ErrorBoundaryProps {
  children: ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
  error: string;
  stack: string;
}

interface ErrorViewProps {
  error: string;
  stack: string;
  resetError: () => void;
}

const ErrorView: FC<ErrorViewProps> = (props: ErrorViewProps): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const goBack = () => {
    navigate(-1);
    props.resetError();
  };
  const retry = () => {
    props.resetError();
  };
  return (
    <Container>
      <Toolbar />
      <h1>{t('ERROR_BOUNDARY_PAGE_TITLE')}</h1>
      <Stack spacing={2} direction="column">
        <span>{t('ERROR_BOUNDARY_PAGE_DESCRIPTION')}</span>
        <Alert severity="error">
          <AlertTitle>{props.error}</AlertTitle>
          <details>
            <summary>{t('HEADING_ERROR_STACK_DETAILS')}</summary>
            <pre className="api-response">{decodeURI(props.stack)}</pre>
          </details>
        </Alert>
        <Stack spacing={2} direction="row">
          <Button variant="contained" color="primary" onClick={goBack}>
            {t('BUTTON_BACK')}
          </Button>
          <Button variant="contained" color="primary" onClick={retry}>
            {t('BUTTON_RETRY')}
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
};

// https://reactjs.org/docs/error-boundaries.html#introducing-error-boundaries
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static contextType = appContext;
  context!: ContextType<typeof appContext>;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: '',
      stack: '',
    };
    this.resetError = this.resetError.bind(this);
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // You can also log the error to an error reporting service
    this.context.setAppLoading(false);
    this.setState({
      hasError: true,
      error: error.message,
      stack: errorInfo.componentStack ?? '',
    });
    errorHandler(error);
  }

  resetError(): void {
    this.setState({ hasError: false });
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <ErrorView
          error={this.state.error}
          stack={this.state.stack}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}
ErrorBoundary.contextType = appContext;

export default ErrorBoundary;
